/**
 * Central registry for landing-page hero variants.
 *
 * Single source of truth for: variant keys, URL paths, SEO meta,
 * and the home-page rotation order. To add a new feature landing
 * page (e.g. /alpha) the only changes are:
 *
 *   1. Add a key + config to FEATURE_VARIANTS below
 *   2. Add the i18n copy to:
 *        - src/i18n/locales/en.json   landingNew.hero.variants.<key>
 *        - src/i18n/locales/ar.json   landingNew.hero.variants.<key>
 *   3. (optional) Add the key to HOME_ROTATION if it should rotate on `/`
 *
 * App.tsx auto-generates routes from FEATURE_VARIANTS, landing-new.tsx
 * reads SEO from VARIANT_SEO, and LandingNewHero.tsx imports the
 * `HeroVariant` type from here. No new files or type unions to update.
 */

/**
 * Every variant the hero understands. `default` is the canonical
 * landing on `/`; `taaft` is the partnership variant served by
 * TaaftLanding via /taaft, /exclusive, /special, /deal — neither
 * has a feature-page path. Feature variants follow.
 */
export type HeroVariant =
  | "default"
  | "taaft"
  | "scanner"
  | "alerts"
  | "backtesting"
  | "watchlist"
  | "byoai"
  | "portfolio"
  | "research"
  | "ksa";

/**
 * Variant keys that have their own feature-page path.
 * Excludes `default` (lives on `/`) and `taaft` (lives on /taaft etc.).
 */
export type FeatureVariant = Exclude<HeroVariant, "default" | "taaft">;

interface FeatureVariantConfig {
  /** Primary path under root, e.g. "/scanner" */
  path: string;
  /** Optional alternate paths that route to the same variant */
  aliases?: string[];
}

/**
 * Configuration for variants that have dedicated feature-page URLs.
 * Iterating this in App.tsx generates the routes automatically.
 */
export const FEATURE_VARIANTS: Record<FeatureVariant, FeatureVariantConfig> = {
  scanner: { path: "/scanner" },
  alerts: { path: "/alerts" },
  backtesting: { path: "/backtesting", aliases: ["/backtest"] },
  watchlist: { path: "/watchlist" },
  byoai: { path: "/byoai" },
  portfolio: { path: "/portfolio" },
  research: { path: "/research" },
  ksa: { path: "/ksa" },
};

/**
 * SEO meta per variant — title and description used in the page's
 * <Helmet>. Includes default + taaft so landing-new.tsx has a single
 * complete map. Per-language SEO is not implemented here; if needed,
 * extend this to a Record<HeroVariant, Record<"en" | "ar", ...>>.
 */
export const VARIANT_SEO: Record<
  HeroVariant,
  { title: string; description: string }
> = {
  default: {
    title: "MiDash — The conversational trading desk",
    description:
      "The conversational trading desk. AI agents that scan, alert, and trade in plain language. Test your ideas on real tick data, live across your brokers.",
  },
  taaft: {
    title: "MiDash — The conversational trading desk",
    description:
      "The conversational trading desk. AI agents that scan, alert, and trade in plain language. Test your ideas on real tick data, live across your brokers.",
  },
  scanner: {
    title: "MiDash Scanner — Don't trade the market. Outsmart it.",
    description:
      "Your AI assistant brings the data, the patterns, and the test results. You bring the call. Together you make moves the market doesn't see coming.",
  },
  alerts: {
    title: "MiDash Alerts — We'll tell you when not to trade.",
    description:
      "No nudges. Just an honest second opinion before your money moves.",
  },
  backtesting: {
    title: "MiDash Backtesting — Hypothesis. Test. Trade.",
    description:
      "Bring an idea. Your AI assistant tests it against years of real market data. You see the results. You make the move.",
  },
  watchlist: {
    title: "MiDash Watchlist — Better decisions, on demand.",
    description:
      "Ask anything about the market. Test any trade. Act on real data. Your AI assistant is on call, all the time, and it does the homework so you don't have to.",
  },
  byoai: {
    title: "MiDash BYOAI — Bring your own AI.",
    description:
      "Already pay for Claude or ChatGPT? Plug it in.",
  },
  portfolio: {
    title: "MiDash Portfolio Analysis — Stop guessing. Start knowing.",
    description:
      "Every trade you've ever made was a guess of some size. The wins, the losses, all of it. Your AI assistant tests your idea against decades of real data so you actually know what you're walking into.",
  },
  research: {
    title: "MiDash Research — All the experience. None of the years.",
    description:
      "Your AI assistant has studied every market move from the past few decades. So your first trade can be as informed as a thirty-year veteran's tenth.",
  },
  ksa: {
    title: "MiDash KSA — Saudi markets, in your language.",
    description:
      "Trade Tadawul. Al-Rajhi, Aramco, SABIC, and more. In conversational Arabic. One assistant. One conversation.",
  },
};

/**
 * Order of variants the hero rotates through on `/`. The first entry
 * is what crawlers and first-paint see, so keep `default` at index 0.
 * Drop a key from this list to remove it from rotation without
 * unpublishing its feature page.
 */
export const HOME_ROTATION: HeroVariant[] = [
  "default",
  "scanner",
  "alerts",
  "backtesting",
  "watchlist",
  "byoai",
  "portfolio",
  "research",
  "ksa",
];
